import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useState } from 'react';
import UpdatedTable from '../../components/updated-table/StyledTable';
import SearchIcon from '@mui/icons-material/Search';
import { FileDownloadOutlined, FilterAlt, Refresh } from "@mui/icons-material";
import { COLORS, jsonToXlsx, output } from '../../settings/themeSettings';
import { Search, SearchIconWrapper, StyledInputBase } from '../BFacturation/BFacturation';
import { Typography, Box } from '@mui/material';
import { fetchPaiments } from '../../data/slices/paiementsSlice';
import { BlobProvider } from '@react-pdf/renderer';
import JournalPDF from '../../components/JournalPDF/JournalPDF';



const PaymentPage = (props) => {
    const { paiements, fetchPaiementStatus } = useSelector((state) => state.paiements);
    const dispatch = useDispatch();
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [activite, setActivite] = useState('tous');

    const [searchQuery, setSearchQuery] = useState('');

    const normalizeString = (str) => {
        return (str || '').toString()
            .toLowerCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '') // retire les accents
            .replace(/[^\w\s]/g, '') // retire les caractères spéciaux sauf espace
            .replace(/\s+/g, ' ')
            .trim();
    };

    const fuzzyMatch = (query, text) => {
        if (!query || !text) return false;
        const q = normalizeString(query);
        const t = normalizeString(text);
        if (t.includes(q)) return true;
        // Simple fuzzy: chaque caractère de query doit apparaître dans l’ordre dans text
        let idx = 0;
        for (const char of q) {
            idx = t.indexOf(char, idx);
            if (idx === -1) return false;
            idx++;
        }
        return true;
    };

    const formatActivite = (type) => {
        if (!type) return 'Tourisme';
        const t = String(type).toLowerCase();
        if (t === 'billetterie') return 'Billetterie';
        if (t === 'circuit') return 'Circuit';
        if (t === 'sejours' || t === 'séjours') return 'Séjours';
        if (t === 'transfert') return 'Transfert';
        return type.charAt(0).toUpperCase() + type.slice(1);
    };

    const filterMethod = (item, query) => {
        if (!query) return true;
        const searchTerms = query.toLowerCase().split(' ').filter(term => term.length > 0);

        const facturaStr = item.facture ? String(item.facture) : '';
        const montantStr = item.montant ? String(item.montant) : '';
        const methodeStr = item.methode ? String(item.methode) : '';
        const commentaireStr = item.commentaire ? String(item.commentaire) : '';
        const dateStr = item.date ? String(item.date) : '';
        const rawActivite = item.typeActivite || (item.facture?.numeroFacture ? 'Billetterie' : 'Tourisme');
        const activiteStr = formatActivite(rawActivite);

        // Fusionne tous les champs pour une recherche globale
        const fullText = [facturaStr, montantStr, methodeStr, commentaireStr, dateStr, activiteStr].join(' ');

        return searchTerms.every(term => fuzzyMatch(term, fullText));
    };

    useEffect(() => {
        setStartDate('');
        setEndDate('');
        dispatch(fetchPaiments());
    }, [dispatch]);

    useEffect(() => {
        if (!!startDate && !!endDate) {
            const startYear = parseInt(startDate.split('-')[0]);
            const endYear = parseInt(endDate.split('-')[0]);
            
            // On ne déclenche l'appel API que si les années sont cohérentes
            if (startYear > 1990 && startYear < 2100 && endYear > 1990 && endYear < 2100) {
                dispatch(fetchPaiments({ startDate, endDate }));
            }
        }
    }, [startDate, endDate, dispatch]);

    const displayRows = [...paiements]
        .map(p => {
            const raw = p.typeActivite || (p.facture?.numeroFacture ? 'billetterie' : 'tourisme');
            return { ...p, typeActivite: formatActivite(raw) };
        })
        .filter(p => {
             if (activite === 'tous') return true;
             if (activite.toLowerCase() === 'tourisme') {
                 return p.typeActivite.toLowerCase() !== 'billetterie';
             }
             return p.typeActivite.toLowerCase() === activite.toLowerCase();
        })
        .filter(p => filterMethod(p, searchQuery))
        .reverse();

    return (fetchPaiementStatus === 'loading') ?
        <div className="loader-container">
            <span className="loader"></span>
        </div>
        : <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100vh',
            overflow: 'hidden',
            backgroundColor: 'background.default',
        }}>
            {/* ── PARTIE SUPÉRIEURE FIGÉE ── */}
            <Box sx={{
                flexShrink: 0,
                backgroundColor: 'background.paper',
                zIndex: 110,
                boxShadow: '0 2px 4px rgba(0,0,0,0.08)',
                borderBottom: '1px solid rgba(0,0,0,0.06)',
                padding: '12px 20px 8px',
            }}>
                <div className="header">
                    <div className="title">
                        Journal de paiements
                    </div>
                </div>

                <div className="table-header">
                    <div className="tools">
                        <div className="btn" onClick={() => {
                            setEndDate('');
                            setStartDate('');
                            setActivite('tous');
                            setSearchQuery('');
                            dispatch(fetchPaiments());
                        }}>
                            <Refresh style={{ fill: COLORS.secondary }} />
                            <Typography style={{ color: COLORS.secondary, fontSize: '1em', fontWeight: 'bold' }} >Réinitialiser</Typography>
                        </div>

                        <div className="btn">
                            <FilterAlt style={{ fill: COLORS.secondary }} />
                            <select value={activite} onChange={(e) => setActivite(e.target.value)} style={{ padding: '5px', borderRadius: '4px', border: `1px solid ${COLORS.secondary}44` }}>
                                <option value="tous">Toutes Activités</option>
                                <option value="Billetterie">Billetterie</option>
                                <option value="Circuit">Circuit</option>
                                <option value="Sejours">Séjours</option>
                                <option value="Transfert">Transfert</option>
                                <option value="Tourisme">Tourisme (Autres)</option>
                            </select>
                        </div>
                        <div className="btn">
                            <span>De</span>
                            <input type="date" value={startDate} onChange={(e) => {
                                setStartDate(e.target.value);
                            }} />
                            <span>à</span>

                            <input type="date" value={endDate} onChange={(e) => {
                                output(endDate);
                                setEndDate(e.target.value);
                            }} />
                        </div>
                        <div className="btn" onClick={
                            () => {
                                if (paiements && paiements.length > 0) {
                                    jsonToXlsx(paiements, "Journal de paiement");
                                }
                            }
                        }>
                            <FileDownloadOutlined style={{ fill: COLORS.secondary }} />
                            <Typography style={{ color: COLORS.secondary, fontSize: '1em', fontWeight: 'bold' }} >Excel</Typography>
                        </div>

                        <div className="btn"
                            onClick={() => {
                                document.getElementById('journalp')?.click();
                            }}
                        >
                            <FileDownloadOutlined style={{ fill: COLORS.secondary }} />
                            <BlobProvider document={<JournalPDF data={[...paiements].reverse()} heads={['facture', 'date', 'montant', 'methode', 'commentaire']} title="Journal de Paiement" />}>
                                {({ blob, url }) => (
                                    <a href={url}
                                        id='journalp'
                                        style={{
                                            cursor: 'pointer',
                                            textDecoration: 'none',
                                            fontSize: '1em',
                                            color: COLORS.secondary,
                                            fontWeight: 'bold'
                                        }}
                                        target="_blank" rel="noreferrer"
                                    >Imprimer</a>
                                )}
                            </BlobProvider>

                        </div>
                    </div>
                    <div className="searchbar">
                        <Search>
                            <SearchIconWrapper>
                                <SearchIcon style={{ fill: COLORS.secondary }} />
                            </SearchIconWrapper>
                            <StyledInputBase
                                placeholder="Recherche (numéro facture, montant, mode, commentaire, date...)"
                                inputProps={{ 'aria-label': 'search' }}
                                value={searchQuery}
                                onChange={(e) => {
                                    setSearchQuery(e.target.value);
                                }}
                            />
                        </Search>
                    </div>
                </div >
            </Box>

            {/* ── ZONE DE DÉFILEMENT — tableau avec en-têtes figés ── */}
            <Box sx={{
                flex: 1,
                overflow: 'auto',
                padding: '0 20px 20px',
                // Scrollbar discrète
                '&::-webkit-scrollbar': { width: '6px', height: '6px' },
                '&::-webkit-scrollbar-track': { background: '#f1f5f9' },
                '&::-webkit-scrollbar-thumb': {
                    background: 'rgba(0,0,0,0.2)',
                    borderRadius: '3px',
                    '&:hover': { background: 'rgba(0,0,0,0.35)' }
                }
            }}>
                <UpdatedTable
                    rows={displayRows}
                    head={['facture', 'date', 'montant', 'methode', 'activite', 'commentaire']}
                    headSubs={{ "methode": "mode", "activite": "type" }}
                    totals={["montant"]}
                    totalPosition="top"
                />
            </Box>
        </Box>;







}


export default PaymentPage;
